const firstDefined = (...values) => values.find((value) => value !== undefined);

const asArray = (value) => (Array.isArray(value) ? value : []);

const coverageId = (coverage) => {
  const nestedCoverage =
    coverage.coverageGroupCompany || coverage.coverage || coverage;
  return firstDefined(
    coverage.coverageGroupCompanyId,
    nestedCoverage.coverageGroupCompanyId,
    nestedCoverage.id,
    coverage.id
  );
};

const coverageName = (coverage) => {
  const nestedCoverage =
    coverage.coverageGroupCompany || coverage.coverage || coverage;
  return (
    firstDefined(
      coverage.name,
      coverage.coverageName,
      nestedCoverage.name,
      nestedCoverage.coverageName
    ) || 'Cobertura'
  );
};

const policyNumber = (coverage) =>
  firstDefined(
    coverage.policyNumber,
    coverage.numberPolicy,
    coverage.policy?.numberPolicy,
    coverage.digitalFormPolicy?.numberPolicy
  );

const groupName = (coverage) =>
  firstDefined(
    coverage.groupName,
    coverage.groupCompanyName,
    coverage.groupCompany?.name,
    coverage.digitalFormPolicy?.groupCompany?.name
  );

const allowsDependents = (coverage) => {
  const value = firstDefined(
    coverage.allowDependents,
    coverage.coverage?.allowDependents
  );

  return value === true || value === 1 || value === 'true';
};

const normalizeId = (id) => {
  if (typeof id !== 'string' || id.trim() === '' || Number.isNaN(Number(id))) {
    return id;
  }
  return Number(id);
};

const idsFromValue = (value) =>
  asArray(value).map((coverage) =>
    normalizeId(typeof coverage === 'object' ? coverageId(coverage) : coverage)
  );

const responseBody = (response) => {
  if (Array.isArray(response)) {
    return { dependents: response };
  }
  if (Array.isArray(response?.data)) {
    return { dependents: response.data };
  }
  if (response?.data) {
    return response.data;
  }
  return response || {};
};

export const normalizeDependentCoverageAssignments = (response) => {
  const body = responseBody(response);
  const dependents = asArray(body.dependents || body.dependants);
  const globalCoverages = asArray(
    body.eligibleCoverages || body.availableCoverages || body.coverages
  );
  const globalSelectedCoverageIds = firstDefined(
    body.selectedCoverageIds,
    body.currentCoverageIds,
    body.coverageGroupCompanyIds
  );
  const selectedCoverageIds = new Set(idsFromValue(globalSelectedCoverageIds));
  const hasGlobalSelection = globalSelectedCoverageIds !== undefined;
  const assignments = asArray(
    body.assignments || body.coverageAssignments || body.coverageSelections
  );
  const assignmentByInsuredId = new Map(
    assignments.map((assignment) => [
      firstDefined(assignment.insuredId, assignment.dependentId),
      assignment
    ])
  );

  return dependents.map((dependent, index) => {
    const insured = dependent.insured || dependent.dependent || {};
    const insuredId = firstDefined(
      dependent.insuredId,
      dependent.dependentId,
      insured.id,
      dependent.id
    );
    const availableCoverages = asArray(
      dependent.eligibleCoverages ||
        dependent.availableCoverages ||
        dependent.coverages ||
        globalCoverages
    )
      .filter(allowsDependents)
      .map((coverage) => ({
        id: normalizeId(coverageId(coverage)),
        name: coverageName(coverage),
        required: Boolean(
          coverage.dependentRequired || coverage.coverage?.dependentRequired
        ),
        policyId: normalizeId(
          firstDefined(
            coverage.policyId,
            coverage.digitalFormPolicy?.policyId,
            coverage.policy?.id
          )
        ),
        policyNumber: policyNumber(coverage),
        groupCompanyId: normalizeId(
          firstDefined(
            coverage.groupCompanyId,
            coverage.digitalFormPolicy?.groupCompanyId,
            coverage.groupCompany?.id
          )
        ),
        groupName: groupName(coverage)
      }))
      .filter((coverage) => coverage.id !== undefined && coverage.id !== null)
      .filter(
        (coverage) =>
          !hasGlobalSelection || selectedCoverageIds.has(coverage.id)
      );

    const persistedAssignment = assignmentByInsuredId.get(insuredId) || {};
    const selected = idsFromValue(
      firstDefined(
        dependent.selectedCoverageIds,
        dependent.currentCoverageIds,
        dependent.selectedCoverages,
        dependent.currentCoverages,
        dependent.currentCoverageSelections,
        persistedAssignment.selectedCoverageIds,
        persistedAssignment.currentCoverageIds,
        persistedAssignment.selectedCoverages
      )
    );
    const selectedFromCoverageState = asArray(
      dependent.eligibleCoverages || dependent.coverages
    )
      .filter(
        (coverage) =>
          coverage.selected || coverage.isSelected || coverage.current
      )
      .map((coverage) => normalizeId(coverageId(coverage)));
    const currentSelectedIds = selected.length
      ? selected
      : selectedFromCoverageState;

    return {
      key: insuredId ?? index,
      insuredId,
      name:
        dependent.name ||
        dependent.fullName ||
        insured.fullName ||
        [
          dependent.firstName || insured.firstName,
          dependent.secondName || insured.secondName,
          dependent.firstSurname || insured.firstSurname,
          dependent.secondSurname || insured.secondSurname
        ]
          .filter(Boolean)
          .join(' '),
      eligibleCoverages: availableCoverages,
      selectedCoverageIds: currentSelectedIds.filter((id) =>
        availableCoverages.some((coverage) => coverage.id === id)
      )
    };
  });
};
